/* =============================================================================
 * Copyright (c) 2026 SKYXOPS Corp. All rights reserved.
 *
 * This file is proprietary and confidential. Unauthorized copying,
 * distribution, modification, or use of this file, via any medium,
 * is strictly prohibited without the express written permission of
 * SKYXOPS Corp.
 * ============================================================================= */
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, applyThemePreference } from '@skyxops/ui';
import { Monitor, Moon, Sun } from 'lucide-react';
import { updateProfile, type Profile } from '../../api';

type Pref = 'light' | 'dark' | 'system';
const OPTIONS: { v: Pref; label: string; Icon: typeof Sun }[] = [
  { v: 'light', label: 'Light', Icon: Sun },
  { v: 'dark', label: 'Dark', Icon: Moon },
  { v: 'system', label: 'System', Icon: Monitor },
];

// Shared cross-app helper (AI-UI-102) — same localStorage key + html.dark flip.
const apply = applyThemePreference;

export function ThemeSection({ profile }: { profile: Profile }) {
  const stored = (window.localStorage.getItem('skyxops-theme') as Pref | null);
  const [pref, setPref] = useState<Pref>(profile.theme_preference ?? stored ?? 'system');

  function choose(p: Pref) {
    setPref(p);
    apply(p);
    void updateProfile({ theme_preference: p }).catch(() => { /* best-effort cross-device */ });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Appearance</CardTitle>
        <CardDescription>Choose how SKYXOPS looks. Saved to your account and applied on every device.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="inline-flex rounded-lg border border-border bg-muted p-1">
          {OPTIONS.map(({ v, label, Icon }) => (
            <button key={v} type="button" onClick={() => choose(v)}
              className={`inline-flex items-center gap-2 rounded-md px-4 py-1.5 text-sm transition-colors ${
                pref === v ? 'bg-card font-semibold text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
